import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ClientApiService } from '../../services/client-api/ClientApiService';
import {
  Client,
  ClientStatus,
  ClientListFilters,
} from '../../types/Client';

type ClientSortField = 'client' | 'subscription' | 'orders' | 'total' | 'status' | 'owner';

@Component({
  selector: 'app-clients-page',
  standalone: false,
  templateUrl: './clients-page.component.html',
  styleUrl: './clients-page.component.sass',
})
export class ClientsPageComponent implements OnInit {
  public clients: Client[] = [];
  public filters: ClientListFilters = {
    search: '',
    status: '',
    page: 1,
    pageSize: 20,
  };
  public readonly pageSizeOptions = [
    20,
    50,
    100,
  ];
  public total = 0;
  public loading = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public sortField: ClientSortField = 'status';
  public sortDirection: 'asc' | 'desc' = 'desc';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  public constructor(
    private clientApi: ClientApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadClients();
  }

  public get totalPages(): number {
    return Math.max(Math.ceil(this.total / this.filters.pageSize), 1);
  }

  public get sortedClients(): Client[] {
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    return [
      ...this.clients,
    ].sort((left, right) => {
      const byDefaultDate = new Date(right.updatedAt || right.createdAt).getTime() - new Date(left.updatedAt || left.createdAt).getTime();
      const statusCompare = Number(left.status === 'active') - Number(right.status === 'active');

      if (this.sortField === 'status') {
        return (statusCompare || byDefaultDate) * direction;
      }
      if (this.sortField === 'client') {
        return this.getClientName(left).localeCompare(this.getClientName(right), 'ru') * direction || byDefaultDate;
      }
      if (this.sortField === 'subscription') {
        return this.getSubscriptionText(left).localeCompare(this.getSubscriptionText(right), 'ru') * direction || byDefaultDate;
      }
      if (this.sortField === 'owner') {
        return this.getOwnerName(left).localeCompare(this.getOwnerName(right), 'ru') * direction || byDefaultDate;
      }
      if (this.sortField === 'orders') {
        return ((left.ordersTotal ?? 0) - (right.ordersTotal ?? 0)) * direction || byDefaultDate;
      }
      if (this.sortField === 'total') {
        return ((left.totalPaidAmount ?? 0) - (right.totalPaidAmount ?? 0)) * direction || byDefaultDate;
      }

      return byDefaultDate;
    });
  }

  public async loadClients(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.clientApi.getClients(this.filters);
      const result = this.unwrapResponse(response);
      if (result) {
        this.clients = result.clients;
        this.total = result.total;
        this.filters.page = result.page;
        this.filters.pageSize = result.pageSize;
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  public async applyFilters(): Promise<void> {
    this.filters.page = 1;
    await this.loadClients();
  }

  public queueSearch(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(() => {
      void this.applyFilters();
    }, 260);
  }

  public async setStatusFilter(status: ClientStatus | ''): Promise<void> {
    this.filters.status = status;
    await this.applyFilters();
  }

  public async goToPage(page: number): Promise<void> {
    this.filters.page = Math.min(Math.max(page, 1), this.totalPages);
    await this.loadClients();
  }

  public async setPageSize(pageSize: number): Promise<void> {
    this.filters.pageSize = pageSize;
    await this.applyFilters();
  }

  public setSort(field: ClientSortField): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortField = field;
    this.sortDirection = field === 'status' ? 'desc' : 'asc';
  }

  public sortState(field: ClientSortField): string {
    if (this.sortField !== field) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public getClientRoute(client: Client): string {
    return client.publicIdentifier || client.id;
  }

  public getClientName(client: Client): string {
    return `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || 'Клиент';
  }

  public getOwnerName(client: Client): string {
    return client.owner?.displayName || client.owner?.username || 'Не назначен';
  }

  public getSubscriptionText(client: Client): string {
    if (client.teams.length === 0) {
      return 'Нет подписки';
    }

    return client.teams.map((team) => team.name).join(', ');
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
